/**
 * facets.js — Filtres et tri (collection / recherche).
 *
 * Un seul formulaire GET : déplacé entre sidebar desktop et panneau mobile inline.
 * Mobile collection : pills → panneau déroulant sous les pills (maquette).
 */

(() => {
  'use strict';

  const root = document.querySelector('[data-facets-root]');
  if (!root) return;

  const form = root.querySelector('[data-facets-form]') || document.getElementById('FacetsForm');
  if (!form) return;

  const sectionId = root.closest('.shopify-section')?.id.replace('shopify-section-', '');
  const desktop = window.matchMedia('(min-width: 990px)');
  const sidebar = root.querySelector('.collection-layout__aside, [data-facets-sidebar]');
  const mobileInline = root.querySelector('[data-facets-mobile-inline]');
  const drawerSlot = document.querySelector('[data-facets-mobile]');

  let controller = null;

  const placeForm = () => {
    const target = desktop.matches
      ? sidebar
      : mobileInline || drawerSlot || sidebar;
    if (!target) return;
    if (form.parentElement !== target) target.appendChild(form);

    if (!desktop.matches && mobileInline) {
      form.querySelectorAll('details.facet').forEach((details) => {
        details.open = false;
      });
      root.querySelectorAll('[data-facet-tab]').forEach((tab) => {
        tab.setAttribute('aria-pressed', 'false');
      });
    }
  };

  /* lmdh 30/09/2026 (rapport 334, C2 et C10) : filtre Coloris. Rangee de familles en tete (un toucher coche toutes
     les valeurs de la famille, une seule mise a jour), gris et neutres d abord ; filtre Taille range par largeur. */
  const PRIORITE = ['gris anthracite', 'gris', 'gris clair', 'noir', 'beige'];
  const nomDe = (li) => li?.querySelector('.facet__label, .facet__swatch-name')?.textContent.trim() || '';
  // lmdh 30/09/2026 (retours du proprietaire) : un coloris hors table n est jamais perdu, il tombe dans « Autres coloris »
  const AUTRES = { cle: 'autres', nom: 'Autres coloris', couleur: '#E8E4DC' };
  const famillesDe = () => [...window.LMH.FAMILLES, AUTRES];
  const familleDe = (nom) => window.LMH.familleColoris(nom) || AUTRES;
  const rangColoris = (nom) => {
    const p = PRIORITE.indexOf(nom.toLowerCase());
    if (p >= 0) return p;
    const f = window.LMH?.familleColoris?.(nom);
    return 10 + (f ? window.LMH.FAMILLES.indexOf(f) : window.LMH?.FAMILLES?.length || 99);
  };
  const ranger = (liste, rang) => {
    [...liste.children]
      .map((li, i) => ({ li, i, r: rang(nomDe(li)) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .forEach(({ li }) => liste.appendChild(li));
  };
  const entreesFamille = (facet, f) =>
    [...facet.querySelectorAll('input.facet__input')].filter(
      (i) => !i.disabled && familleDe(nomDe(i.closest('li'))) === f
    );
  const enrichirFiltres = () => {
    form.querySelectorAll('details.facet').forEach((facet) => {
      const liste = facet.querySelector('.facet__values');
      if (!liste || liste.dataset.enrichi) return;
      liste.dataset.enrichi = 'true';
      const etiquette = (facet.dataset.facetLabel || '').toLowerCase();
      if (etiquette === 'taille') {
        ranger(liste, (nom) => { const m = nom.match(/\((\d+)/); return m ? Number(m[1]) : 9999; });
        return;
      }
      if (!facet.classList.contains('facet--swatch') || !window.LMH?.FAMILLES) return;
      ranger(liste, rangColoris);
      // lmdh 30/09/2026 (retours du proprietaire) : le filtre ne montre plus que les couleurs generales (« Gris »
      // coche tous les gris). La liste detaillee reste dans le formulaire, cachee : ce sont ses cases qui partent
      // dans l adresse de la page.
      const presentes = famillesDe().filter((f) => entreesFamille(facet, f).length > 0);
      if (!presentes.length) return;
      facet.classList.add('facet--familles');
      const rangee = document.createElement('div');
      rangee.className = 'facet__familles';
      rangee.setAttribute('role', 'group');
      rangee.setAttribute('aria-label', 'Couleurs');
      presentes.forEach((f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'facet__famille';
        b.dataset.famille = f.cle;
        const entrees = entreesFamille(facet, f);
        const n = entrees.filter((i) => i.checked).length;
        b.setAttribute('aria-pressed', n === 0 ? 'false' : n === entrees.length ? 'true' : 'mixed');
        b.innerHTML = '<span class="facet__famille-dot" style="background:' + f.couleur + '"></span>';
        b.append(f.nom);
        rangee.appendChild(b);
      });
      liste.before(rangee);
      // le compteur du titre compte des couleurs, plus des valeurs (« 27 » pour un seul geste sur « Gris »)
      const compteur = facet.querySelector('.facet__active-count');
      if (compteur) compteur.textContent = String(presentes.filter((f) => entreesFamille(facet, f).some((i) => i.checked)).length);
    });
  };

  // lmdh 30/09/2026 (retours du proprietaire) : le rappel des filtres actifs parle en couleurs generales, une puce
  // par couleur (« Gris × »), au lieu d une puce par valeur ou d une puce « Coloris (27) ».
  const regrouperPuces = () => {
    const facet = form.querySelector('details.facet--familles');
    const zone = root.querySelector('[data-facets-chips] .chips');
    if (!facet || !zone) return;
    const anciennes = [...zone.querySelectorAll('li[data-chip-filter="' + facet.getAttribute('data-facet-id') + '"]')];
    if (!anciennes.length) return;
    const icone = anciennes[0].querySelector('svg')?.outerHTML || '';
    famillesDe()
      .filter((f) => entreesFamille(facet, f).some((i) => i.checked))
      .forEach((f) => {
        const li = document.createElement('li');
        li.innerHTML = '<button type="button" class="chip" data-chip-famille="' + f.cle + '"><span></span>' + icone + '<span class="visually-hidden"></span></button>';
        li.querySelector('span').textContent = f.nom;
        li.querySelector('.visually-hidden').textContent = 'Retirer le filtre ' + f.nom;
        anciennes[0].before(li);
      });
    anciennes.forEach((li) => li.remove());
  };

  root.addEventListener('click', (event) => {
    const b = event.target.closest('.facet__famille, [data-chip-famille]');
    if (!b || !window.LMH?.FAMILLES) return;
    event.preventDefault();
    const facet = b.closest('details.facet') || form.querySelector('details.facet--familles');
    const cle = b.dataset.famille || b.dataset.chipFamille;
    const f = famillesDe().find((x) => x.cle === cle);
    if (!facet || !f) return;
    const entrees = entreesFamille(facet, f);
    // bouton du filtre : coche toute la couleur, ou la decoche si elle l est deja ; puce du rappel : la retire
    const cocher = b.dataset.chipFamille ? false : !(entrees.length > 0 && entrees.every((i) => i.checked));
    entrees.forEach((i) => { i.checked = cocher; });
    refresh(buildUrl(), { recaler: true });
  });

  placeForm();
  enrichirFiltres();
  regrouperPuces();
  desktop.addEventListener('change', placeForm);

  const buildUrl = () => {
    const data = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      if (String(value).trim() === '') continue;
      params.append(key, value);
    }

    params.delete('page');

    const query = params.toString();
    return window.location.pathname + (query ? `?${query}` : '');
  };

  const results = () => root.querySelector('[data-facets-results]');

  const syncMobileTabs = () => {
    if (desktop.matches || !mobileInline) return;
    const open = form.querySelector('details.facet[open]');
    const openId = open?.getAttribute('data-facet-id');
    root.querySelectorAll('[data-facet-tab]').forEach((tab) => {
      tab.setAttribute(
        'aria-pressed',
        tab.getAttribute('data-facet-tab') === openId ? 'true' : 'false'
      );
    });
  };

  const refresh = async (url, { push = true, recaler = false } = {}) => {
    const container = results();
    container?.classList.add('is-loading');
    container?.setAttribute('aria-busy', 'true');

    controller?.abort();
    controller = new AbortController();

    try {
      const target = new URL(url, window.location.origin);
      const request = new URL(target);
      if (sectionId) request.searchParams.set('section_id', sectionId);

      const response = await fetch(request, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const nextResults = doc.querySelector('[data-facets-results]');
      if (nextResults && container) container.innerHTML = nextResults.innerHTML;

      const nextForm = doc.querySelector('[data-facets-form]');
      if (nextForm) {
        const openIds = new Set(
          [...form.querySelectorAll('details.facet')]
            .filter((d) => d.open)
            .map((d) => d.getAttribute('data-facet-id'))
        );
        // lmdh 30/09/2026 (rapport 334, C1) : la liste d un filtre long (Coloris, 100 valeurs) repartait
        // en haut a chaque remplacement du formulaire ; on garde sa position.
        const listes = new Map(
          [...form.querySelectorAll('details.facet')].map((d) => [
            d.getAttribute('data-facet-id'),
            d.querySelector('.facet__values')?.scrollTop || 0,
          ])
        );
        form.innerHTML = nextForm.innerHTML;
        form.querySelectorAll('details.facet').forEach((details) => {
          const id = details.getAttribute('data-facet-id');
          details.open = openIds.has(id);
          const liste = details.querySelector('.facet__values');
          if (liste && listes.get(id)) liste.scrollTop = listes.get(id);
        });
      }

      ['[data-facets-count]', '[data-facets-chips]'].forEach((selector) => {
        const next = doc.querySelector(selector);
        const current = root.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });

      placeForm();
      syncMobileTabs();
      enrichirFiltres();
      regrouperPuces();

      // lmdh 30/09/2026 (rapport 334, C1) : sur telephone, choisir « Gris » (56e valeur) envoyait la page
      // au pied de page (+2 150 px) : le panneau se referme et l ancrage de defilement suivait la ligne
      // cochee. On ramene la barre des filtres sous l en-tete, les resultats juste dessous.
      if (recaler && !desktop.matches) {
        const barre = root.querySelector('.collection-controls') || results();
        const entete =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const haut = barre ? barre.getBoundingClientRect().top : 0;
        if (barre && (haut < entete || haut > window.innerHeight * 0.5)) {
          window.scrollTo({ top: window.scrollY + haut - entete - 8, behavior: 'instant' });
        }
      }

      if (push) window.history.pushState({ facets: true }, '', target.toString());
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      container?.classList.remove('is-loading');
      container?.removeAttribute('aria-busy');
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    refresh(buildUrl());
  });

  form.addEventListener('change', () => refresh(buildUrl(), { recaler: true }));

  let priceTimer = null;
  form.addEventListener('input', (event) => {
    if (event.target.type !== 'number') return;
    window.clearTimeout(priceTimer);
    priceTimer = window.setTimeout(() => refresh(buildUrl()), 500);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-facets-sort]');
    if (!select || form.contains(select)) return;

    const inForm = form.querySelector('[data-facets-sort]');
    if (inForm) inForm.value = select.value;
    refresh(buildUrl());
  });

  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-remove], .pagination a');
    if (!link) return;
    event.preventDefault();
    refresh(link.href, { recaler: !link.matches('.pagination a') });

    if (link.matches('.pagination a')) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  window.addEventListener('popstate', () => refresh(window.location.href, { push: false }));

  /* Onglets mobiles : panneau inline sous les pills */
  root.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-facet-tab]');
    if (!tab || !root.contains(tab)) return;
    if (desktop.matches) return;

    event.preventDefault();
    const facetId = tab.getAttribute('data-facet-tab');
    const details = form.querySelector(`details.facet[data-facet-id="${facetId}"]`);
    if (!details) return;

    const already = tab.getAttribute('aria-pressed') === 'true';

    form.querySelectorAll('details.facet').forEach((d) => {
      d.open = false;
    });
    root.querySelectorAll('[data-facet-tab]').forEach((t) => {
      t.setAttribute('aria-pressed', 'false');
    });

    if (!already) {
      details.open = true;
      tab.setAttribute('aria-pressed', 'true');
    }
  });
})();
